// components/StudentPortal/Task/StudentTaskWizard.js
// Wizard de tarea (S2, F5). Pantalla propia (no fila-tabla): stepper con pasos
// + nodo "Examen" con candado, paso actual (escena REAL + instruccion +
// respuesta TEXT/VR). Consume el backend F5 (getNextStep con meta de escena).
// El examen final enlaza a F6 (frontend del examen) — placeholder por ahora.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useStudentTask } from "./useStudentTask";
import { StudentExam } from "../Exam/StudentExam";
import { StudentAssistant } from "../Assistant/StudentAssistant";
import styles from "./StudentTaskWizard.module.scss";

export function StudentTaskWizard({ taskId, onBack, mode: deviceMode }) {
  const { t } = useTranslation("students");
  const isVr = deviceMode === "vr";
  const {
    loading,
    error,
    task,
    steps,
    finalQuizRequired,
    finalQuizDone,
    finalQuizId,
    assistant,
    nextStep,
    allDone,
    sending,
    submitStep,
    reload,
    trackStepSceneOpen,
  } = useStudentTask(taskId);

  const [mode, setMode] = useState("TEXT");
  const [text, setText] = useState("");
  const [examOpen, setExamOpen] = useState(false);

  const onToggleVr = () => {
    const nextMode = mode === "VR" ? "TEXT" : "VR";
    setMode(nextMode);
    setText(nextMode === "VR" ? t("tasks.vrCompleted") : "");
  };

  const canSubmit = mode === "VR" || text.trim().length > 0;

  const onSubmit = async () => {
    if (!canSubmit) return;
    await submitStep(
      mode,
      text || (mode === "VR" ? t("tasks.vrCompleted") : ""),
    );
    setMode("TEXT");
    setText("");
  };

  const openScene = () => {
    if (!nextStep?.sceneEnterUrl) return;
    trackStepSceneOpen();
    // En VR: misma pestana (abrir pestana nueva saca al alumno del visor).
    if (isVr) window.location.href = nextStep.sceneEnterUrl;
    else window.open(nextStep.sceneEnterUrl, "_blank", "noopener,noreferrer");
  };

  // Examen final: se abre en lugar del wizard; al cerrar, recargamos la tarea
  // (para reflejar finalQuiz.done en el stepper).
  if (examOpen && finalQuizId) {
    return (
      <div className={styles.wrap}>
        <button
          type="button"
          className={styles.back}
          onClick={() => {
            setExamOpen(false);
            reload();
          }}
        >
          ‹ {t("newHome.back")}
        </button>
        <StudentExam
          quizId={finalQuizId}
          taskId={taskId}
          mode={deviceMode}
          onClose={() => {
            setExamOpen(false);
            reload();
          }}
          onGoHome={onBack}
        />
      </div>
    );
  }

  // Nodos del stepper: un circulo por paso + nodo Examen (si aplica).
  const currentIdx = steps.findIndex((s) => !s.completed);

  return (
    <div className={`${styles.wrap} ${isVr ? styles.vr : ""}`}>
      <button type="button" className={styles.back} onClick={onBack}>
        ‹ {t("newHome.back")}
      </button>

      {loading ? (
        <div className={styles.state}>{t("tasks.loadingStep")}</div>
      ) : error ? (
        <div className={styles.state}>
          {t("newHome.error")}{" "}
          <button type="button" className={styles.linkBtn} onClick={reload}>
            {t("newHome.retry")}
          </button>
        </div>
      ) : (
        <>
          <h1 className={styles.title}>{task?.title}</h1>

          {/* Stepper */}
          <div className={styles.stepper}>
            {steps.map((s, i) => {
              const isCurrent = i === currentIdx;
              const cls = s.completed
                ? styles.done
                : isCurrent
                  ? styles.current
                  : styles.pending;
              return (
                <div key={s.stepId || i} className={`${styles.node} ${cls}`}>
                  <span className={styles.dot}>
                    {s.completed ? "✓" : i + 1}
                  </span>
                  <span className={styles.nodeLabel}>
                    {t("newHome.stepLabel")} {i + 1}
                  </span>
                </div>
              );
            })}
            {finalQuizRequired && (
              <div
                className={`${styles.node} ${
                  finalQuizDone ? styles.done : styles.locked
                }`}
              >
                <span className={styles.dot}>{finalQuizDone ? "✓" : "🔒"}</span>
                <span className={styles.nodeLabel}>
                  {t("newHome.examNode")}
                </span>
              </div>
            )}
          </div>

          {/* Paso actual o fin */}
          {!allDone && nextStep ? (
            <div className={styles.stepCard}>
              {nextStep.sceneBanner ? (
                <div
                  className={styles.banner}
                  style={{ backgroundImage: `url(${nextStep.sceneBanner})` }}
                >
                  <span className={styles.bannerTitle}>
                    {nextStep.sceneTitle || t("tasks.sceneFallback")}
                  </span>
                </div>
              ) : (
                <h2 className={styles.sceneTitle}>
                  {nextStep.sceneTitle || t("tasks.sceneFallback")}
                </h2>
              )}

              <div className={styles.body}>
                <div className={styles.instruction}>
                  <small>{t("newHome.instructionLabel")}</small>
                  <p>{nextStep.instruction || t("tasks.noDescription")}</p>
                </div>

                <div className={styles.actions}>
                  {nextStep.sceneEnterUrl ? (
                    <button
                      type="button"
                      className={styles.ctaPrimary}
                      onClick={openScene}
                    >
                      {t("newHome.enterScene")}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className={`${styles.toggle} ${mode === "VR" ? styles.toggleOn : ""}`}
                    onClick={onToggleVr}
                  >
                    {mode === "VR"
                      ? t("tasks.vrModeActive")
                      : t("tasks.vrToggle")}
                  </button>
                </div>

                {mode !== "VR" && (
                  <div className={styles.answer}>
                    <label>{t("tasks.yourAnswer")}</label>
                    <textarea
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder={t("tasks.writePlaceholder")}
                      rows={4}
                    />
                  </div>
                )}

                <button
                  type="button"
                  className={styles.ctaPrimary}
                  disabled={sending || !canSubmit}
                  onClick={onSubmit}
                >
                  {t("tasks.sendButton")}
                </button>
              </div>
            </div>
          ) : (
            <div className={styles.doneCard}>
              <h2>{t("tasks.allStepsCompleted")}</h2>
              {finalQuizRequired ? (
                <>
                  <p className={styles.muted}>
                    {finalQuizDone
                      ? t("newHome.examDone")
                      : t("newHome.examReady")}
                  </p>
                  <button
                    type="button"
                    className={styles.ctaPrimary}
                    onClick={() => setExamOpen(true)}
                    disabled={!finalQuizId}
                  >
                    {finalQuizDone
                      ? t("newHome.examRetry")
                      : t("tasks.startFinalExam")}
                  </button>
                </>
              ) : (
                <p className={styles.muted}>{t("tasks.noExamRequired")}</p>
              )}
              <button type="button" className={styles.homeBtn} onClick={onBack}>
                {t("exam.goHome")}
              </button>
            </div>
          )}
        </>
      )}

      {/* Asistente IA de la tarea (opcional): burbuja flotante + chat Gemini */}
      {assistant ? (
        <StudentAssistant assistant={assistant} dark={isVr} bubbleOnly />
      ) : null}
    </div>
  );
}
